import { useEffect, useRef } from 'react';
import className from 'classnames/bind';
import { Splide, SplideSlide, SplideTrack } from 'react-splide-ts';
import { LogosFragment } from '@graphqlTypes';
import { chunkArray } from "@/core/utils";
import Logo from './Logo';

import styles from './Logos.module.scss';
import { Container } from '../Container';
import { CarouselPagination } from '../CarouselPagination';
const cx = className.bind(styles);

const LogoGroup = ({ logos }: { logos: LogosFragment['logos'] }) => {
  return (
    <SplideSlide className={cx("logo-group")}>
      {logos.map((logo, index) => (
        <Logo key={index} logo={logo.logo} />
      ))}
    </SplideSlide>
  );
}

export default function LogoCarousel({
  logos,
}: {
  logos: LogosFragment["logos"];
}) {
  const logoChunks = chunkArray<LogosFragment['logos'][number]>(logos, 4);
  const rootEl = useRef<HTMLDivElement>(null);

  // Override accessibility attributes
  useEffect(() => {
    if (!rootEl.current) return;
    const lists = rootEl.current.querySelectorAll('.splide__list');
    lists.forEach(list => {
      list.setAttribute('role', 'list');
    });
    const slides = rootEl.current.querySelectorAll('.splide__slide');
    slides.forEach(slide => {
      slide.setAttribute('role', 'listitem');
    });
  }, []);

  return (
    <div ref={rootEl}>
      <Splide
        className={cx("logos-splide")}
        options={{
          type: "slide",
          perPage: 3,
          perMove: 3,
          gap: "3rem",
          flickMaxPages: 1,
          flickPower: 250,
          updateOnMove: true,
          padding: {
            left: "5rem",
            right: "5rem",
          },
          breakpoints: {
            1024: {
              perPage: 2,
              perMove: 2,
              arrows: false,
              padding: {
                left: "4rem",
                right: "4rem",
              },
            },
            768: {
              perPage: 1,
              perMove: 1,
              fixedWidth: "86%",
              padding: {
                left: "2rem",
                right: "2rem",
              },
            },
          },
        }}
        hasTrack={false}
      >
        <SplideTrack className={cx("logos-track")}>
          {logoChunks.map((chunk, index) => (
            <LogoGroup key={index} logos={chunk} />
          ))}
        </SplideTrack>
        <Container>
          <CarouselPagination className={cx("logos-pagination")} />
        </Container>
      </Splide>
    </div>
  );
}
